// The turn timeline, for a live share (/s/{id}) and a recording under review
// (/r/{id}) alike. Read-only by construction: a pending permission is
// shown, highlighted, and nothing here can answer it (RFC-043: remote
// approval does not exist). Under review, an `annotator` puts each event's
// threads, and each diff line's, beside what they are about.
import { Fragment, memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { EventBody, Masked as MaskedT } from "../protocol";
import { Markdown } from "../markdown";
import { parseDiff, type DiffLine } from "../review";
import { useTranslation } from "react-i18next";
import { t, money, phrases } from "../i18n";
import { subagentOf, type SeqEvent, type TimelineItem, type Turn } from "../timeline";
import { Badge, type Names } from "./Badge";
import { agentLabel } from "../workbench";

/** What the review view hangs on the timeline: threads per event and per diff line. */
export interface Annotator {
  /**
   * An event's threads and its Comment; `also`, another event of the same
   * item whose threads show there too — a permission's request once it has
   * been answered, so the item has one place for its threads, not two drawn
   * one over the other.
   */
  event: (ev: SeqEvent, also?: SeqEvent) => ReactNode;
  line: (ev: SeqEvent, line: DiffLine) => ReactNode;
  /** How many threads are on an event or its diff's lines: a folded tool with some opens. */
  count?: (ev: SeqEvent) => number;
}

function Slot({ annotator, ev, also }: { annotator?: Annotator; ev?: SeqEvent; also?: SeqEvent }) {
  if (!annotator || !ev) return null;
  return <div className="annotations">{annotator.event(ev, also)}</div>;
}

/**
 * An event folded to its head line — a thought, a tool — that unfolds to
 * what it holds (byj-1wv.51.4). Its threads sit right under the head,
 * folded or not, so its Comment and badge stay on that line and its threads
 * where they were: what unfolds goes under them. A jump to it unfolds it
 * through the <details>, whose own summary is hidden as the head stands for it.
 */
function Folded({ className, seqs, head, threads, initialOpen = false, children }: {
  className: string;
  seqs: string;
  head: ReactNode;
  threads: ReactNode;
  initialOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(initialOpen);
  // something to see arriving later — a thread on a tool's result, polled in
  // — opens it, as it would have been opened had it been there at first
  useEffect(() => {
    if (initialOpen) setOpen(true);
  }, [initialOpen]);
  return (
    <div className={className} data-seqs={seqs}>
      <button type="button" className="item-head fold-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {head}
      </button>
      {threads}
      <details
        className="fold-body"
        open={open}
        // opened by a jump (scrollToSeqs), as well as by the head
        onToggle={(e) => {
          if (e.target === e.currentTarget) setOpen(e.currentTarget.open);
        }}
      >
        <summary hidden />
        {children}
      </details>
    </div>
  );
}

/** A unified diff, line by line; under review each line takes threads. */
function Diff({ ev, lines, annotator }: { ev: SeqEvent; lines: DiffLine[]; annotator?: Annotator }) {
  return (
    <div className="diff" data-testid="diff">
      {lines.map((l) => (
        <Fragment key={l.line}>
          <div className={`diff-line diff-${l.kind}`} data-line={l.line}>
            <code>{l.text || " "}</code>
            {annotator && (l.kind === "add" || l.kind === "del" || l.kind === "ctx") && annotator.line(ev, l)}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

const FollowSlackPx = 48;

function seqAttr(...evs: (SeqEvent | undefined)[]): string {
  return evs
    .filter((e): e is SeqEvent => e !== undefined)
    .map((e) => e.seq)
    .join(" ");
}

function MaskedBadges({ masked }: { masked?: MaskedT[] }) {
  if (!masked || masked.length === 0) return null;
  return (
    <span className="masked" title={t("session:maskedTitle")}>
      {masked.map((m, i) => (
        <span key={i} className="chip chip-masked">
          {t("session:masked", { kind: m.kind, count: m.count })}
        </span>
      ))}
    </span>
  );
}

function Tool({
  item,
  names,
  annotator,
}: {
  item: Extract<TimelineItem, { type: "tool" }>;
  names: Names;
  annotator?: Annotator;
}) {
  const call = item.call?.body;
  const res = item.result?.body;
  const diff = annotator && item.result ? parseDiff(res?.text) : null;
  const name = call?.tool ?? res?.tool ?? "tool";
  const state = res === undefined ? "running" : res.ok === false ? "failed" : "ok";
  const byHuman = call && call.by.kind !== "agent";
  // its arguments' first line beside its name, the rest with the result
  const argLine = (call?.text ?? "").split("\n", 1)[0]!;
  // folded to that line (byj-1wv.51.4), open where there is a diff to read
  // or threads on its result to see; an annotator that cannot count them
  // has every result open, so none of its threads is folded out of sight
  const onResult = item.result === undefined || !annotator ? 0 : annotator.count ? annotator.count(item.result) : 1;
  const open = diff !== null || onResult > 0;
  return (
    <Folded
      className={`item tool tool-${state}`}
      seqs={seqAttr(item.call, item.result)}
      initialOpen={open}
      head={
        <>
          <span className="tool-name">{name}</span>
          {argLine && <code className="tool-arg-line">{argLine}</code>}
          <span className={`chip chip-${state}`}>{state === "running" ? t("session:running") : state === "failed" ? t("session:failed") : t("session:ok")}</span>
          {byHuman && call && <Badge actor={call.by} names={names} />}
          <MaskedBadges masked={call?.masked} />
        </>
      }
      threads={<Slot annotator={annotator} ev={item.call} />}
    >
      {call?.text && <pre className="tool-args">{call.text}</pre>}
      {res && (
        <div className="tool-result">
          <div className="tool-result-head">
            {t("session:toolResult")}
            {res.truncated && <span className="chip chip-truncated">{t("session:truncated")}</span>}
            <MaskedBadges masked={res.masked} />
          </div>
          {diff && item.result ? <Diff ev={item.result} lines={diff} annotator={annotator} /> : <pre>{res.text ?? ""}</pre>}
          <Slot annotator={annotator} ev={item.result} />
        </div>
      )}
    </Folded>
  );
}

function Permission({ item, annotator }: { item: Extract<TimelineItem, { type: "permission" }>; annotator?: Annotator }) {
  const p = item.pending?.body ?? item.resolved?.body;
  const open = item.resolved === undefined;
  return (
    <div
      className={`item permission ${open ? "permission-open" : ""}`}
      data-seqs={seqAttr(item.pending, item.resolved)}
      role={open ? "status" : undefined}
    >
      <div className="item-head">
        <span>{open ? t("session:permissionPending") : t("session:permissionResolved", { decision: item.resolved?.body.reason ?? "" })}</span>
        {p?.tool && <span className="tool-name">{p.tool}</span>}
      </div>
      {item.pending?.body.text && <pre className="tool-args">{item.pending.body.text}</pre>}
      {open && <p className="hint">{t("session:permissionReadOnly")}</p>}
      {/* one place for its threads: the answer's, the request's shown with them */}
      <Slot annotator={annotator} ev={item.resolved ?? item.pending} also={item.resolved ? item.pending : undefined} />
    </div>
  );
}

function Human({ ev, label, names, annotator }: { ev: SeqEvent; label: string; names: Names; annotator?: Annotator }) {
  return (
    <div className={`item human human-${ev.body.kind}`} data-seqs={seqAttr(ev)}>
      <div className="item-head">
        <Badge actor={ev.body.by} names={names} />
        <span className="muted">{label}</span>
      </div>
      <p className="human-text">{ev.body.text}</p>
      <Slot annotator={annotator} ev={ev} />
    </div>
  );
}

function Commit({ ev, annotator }: { ev: SeqEvent; annotator?: Annotator }) {
  const text = ev.body.text ?? "";
  const diff = parseDiff(text);
  // a commit's text is its subject, or (when the source put it there) the diff
  const subject = diff ? text.split("\n", 1)[0]! : text;
  // its own threads under its head line, as a tool's are, and its diff,
  // whose lines take threads of their own, under them (byj-1wv.51.4)
  return (
    <div className="item commit" data-seqs={seqAttr(ev)}>
      <div className="muted commit-head">
        {t("session:commit", { c: ev.body.commit ?? "" })}
        {subject && !subject.startsWith("diff --git") ? ` — ${subject}` : ""}
      </div>
      <Slot annotator={annotator} ev={ev} />
      {diff && <Diff ev={ev} lines={diff} annotator={annotator} />}
    </div>
  );
}

/**
 * What marks each kind of event on a turn's rail (byj-1wv.51.4): read down
 * the rail, a turn is its steps at a glance, without a box round each.
 */
const railIcon: Record<TimelineItem["type"] | "input" | "end", string> = {
  input: "👤",
  thought: "💭",
  tool: "🔧",
  permission: "🔒",
  answer: "💬",
  note: "📝",
  inject: "↪",
  commit: "📦",
  end: "■",
};

/** One step on a turn's rail: its icon on the line, what it is beside it. */
function Rail({ kind, children }: { kind: keyof typeof railIcon; children: ReactNode }) {
  return (
    <div className={`ev ev-${kind}`}>
      <span className="ev-icon" aria-hidden="true">
        {railIcon[kind]}
      </span>
      {children}
    </div>
  );
}

/** An item, under the subagent it is from when it is one's (RFC-044). */
function Item({ item, names, annotator }: { item: TimelineItem; names: Names; annotator?: Annotator }) {
  const sub = subagentOf(item);
  if (!sub) {
    return (
      <Rail kind={item.type}>
        <ItemBody item={item} names={names} annotator={annotator} />
      </Rail>
    );
  }
  return (
    <Rail kind={item.type}>
      <div className="subagent" data-subagent={sub.id}>
        <div className="subagent-label muted small">{t("shares:subagent", { id: sub.id })}</div>
        <ItemBody item={item} names={names} annotator={annotator} />
      </div>
    </Rail>
  );
}

function ItemBody({ item, names, annotator }: { item: TimelineItem; names: Names; annotator?: Annotator }) {
  switch (item.type) {
    case "thought":
      return (
        <Folded className="item thought" seqs={seqAttr(item.ev)} head={t("session:thought")} threads={<Slot annotator={annotator} ev={item.ev} />}>
          <p>{item.ev.body.text}</p>
        </Folded>
      );
    case "tool":
      return <Tool item={item} names={names} annotator={annotator} />;
    case "permission":
      return <Permission item={item} annotator={annotator} />;
    case "answer":
      return (
        <div className="item answer" data-seqs={seqAttr(item.ev)}>
          <Markdown text={item.ev.body.text ?? ""} />
          <Slot annotator={annotator} ev={item.ev} />
        </div>
      );
    case "note":
      return <Human ev={item.ev} label={t("session:noteBy")} names={names} annotator={annotator} />;
    case "inject":
      return <Human ev={item.ev} label={t("session:injectedBy")} names={names} annotator={annotator} />;
    case "commit":
      return <Commit ev={item.ev} annotator={annotator} />;
  }
}

function TurnEnd({ ev, annotator }: { ev: SeqEvent; annotator?: Annotator }) {
  const b: EventBody = ev.body;
  return (
    <div className="turn-end muted" data-seqs={seqAttr(ev)}>
      <span>{t("session:turnEnd", { reason: b.reason ?? "" })}</span>
      {b.usage && (
        // no tokens is a source that said none, an agent share's caller told
        // the cost alone: the cost then, never "0 in · 0 out" (byj-13i)
        <span>
          {b.usage.input_tokens > 0 || b.usage.output_tokens > 0
            ? t("session:usage", { input: b.usage.input_tokens, output: b.usage.output_tokens, cost: money(b.usage.cost_usd) })
            : money(b.usage.cost_usd)}
        </span>
      )}
      <Slot annotator={annotator} ev={ev} />
    </div>
  );
}

/** The one line a folded turn shows: who asked and what, and how it ended (byj-1wv.18). */
function foldedLine(turn: Turn): string {
  const text = (turn.begin?.body.text ?? "").split("\n")[0] ?? "";
  return `${text.length > 100 ? text.slice(0, 100) + "…" : text} — ${outcome(turn)}`;
}

/** How a turn ended, and what it cost: "answered · $0.08", or that it is still running. */
function outcome(turn: Turn): string {
  const cost = turn.end?.body.usage?.cost_usd;
  return phrases.foldedTurn(turn.end !== undefined, cost !== undefined && cost > 0 ? `$${cost.toFixed(2)}` : undefined);
}

/** A person's ratings of the answers to what they asked an agent share (byj-1wv.48). */
export interface Rater {
  /** Whether the person asked this turn, so its answer is theirs to rate. */
  mine(turn: Turn): boolean;
  /** Their rating of the turn begun at seq: 1, -1, or 0 for none. */
  of(seq: number): number;
  set(seq: number, rating: number): void;
}

/** 👍 and 👎 on an answer, pressed again to take it back. */
function Rate({ value, onRate }: { value: number; onRate: (rating: number) => void }) {
  return (
    <span className="rate" role="group" aria-label={t("session:rateAnswer")}>
      <button type="button" className={`link small${value === 1 ? " rate-on" : ""}`} aria-pressed={value === 1} aria-label={t("session:rateUp")} title={t("session:rateUp")} onClick={() => onRate(value === 1 ? 0 : 1)}>
        👍
      </button>
      <button type="button" className={`link small${value === -1 ? " rate-on" : ""}`} aria-pressed={value === -1} aria-label={t("session:rateDown")} title={t("session:rateDown")} onClick={() => onRate(value === -1 ? 0 : -1)}>
        👎
      </button>
    </span>
  );
}

function TurnView({
  turn,
  names,
  annotator,
  rater,
  onPlay,
  open,
  onToggle,
}: {
  turn: Turn;
  names: Names;
  annotator?: Annotator;
  rater?: Rater;
  onPlay?: (turn: number) => void;
  open: boolean;
  onToggle: (turn: number, open: boolean) => void;
}) {
  const begin = turn.begin?.body;
  return (
    <details
      className="turn"
      aria-label={t("session:turn", { n: turn.turn })}
      data-turn={turn.turn}
      open={open}
      // React hands a turn the toggles of the <details> inside it too —
      // thinking, a tool — whose closing is not the turn's
      onToggle={(e) => {
        if (e.target === e.currentTarget) onToggle(turn.turn, e.currentTarget.open);
      }}
    >
      <summary className="turn-head" data-seqs={seqAttr(turn.begin)}>
        <span className="turn-no">{t("session:turn", { n: turn.turn })}</span>
        {begin && <Badge actor={begin.by} names={names} />}
        {/* a workbench's turn says which of its agents it was put to (RFC-057 D5) */}
        {begin?.agent && <span className="turn-agent small">{t("session:turnAgent", { agent: agentLabel(begin.agent) })}</span>}
        {/* folded, what was asked and how it ended; open, how it ended, at the line's end */}
        <span className={`turn-folded muted small${open ? " turn-outcome" : ""}`}>{open ? outcome(turn) : foldedLine(turn)}</span>
        {onPlay && (
          <button
            className="link small"
            onClick={(e) => {
              e.preventDefault();
              onPlay(turn.turn);
            }}
          >
            {t("session:playScreen")}
          </button>
        )}
      </summary>
      {/* the turn's steps down one rail, each marked by its kind (byj-1wv.51.4) */}
      <div className="turn-body">
        {(begin?.text || annotator) && (
          <Rail kind="input">
            {annotator ? (
              // under review, what was asked and its threads: one thing to hover
              <div className="turn-begin">
                {begin?.text && <p className="turn-input">{begin.text}</p>}
                <Slot annotator={annotator} ev={turn.begin} />
              </div>
            ) : (
              <p className="turn-input">{begin!.text}</p>
            )}
          </Rail>
        )}
        {turn.items.map((item, i) => (
          <Item key={i} item={item} names={names} annotator={annotator} />
        ))}
        {turn.end && (
          <Rail kind="end">
            <TurnEnd ev={turn.end} annotator={annotator} />
            {rater && turn.begin && rater.mine(turn) && <Rate value={rater.of(turn.begin.seq)} onRate={(v) => rater.set(turn.begin!.seq, v)} />}
          </Rail>
        )}
      </div>
    </details>
  );
}

/**
 * Light what a jump lands on, and let the light go once it has faded
 * (byj-1wv.51.4): the place found, not a mark left on it. Lit again, it
 * starts over.
 */
export function flash(n: Element): void {
  unflash(n);
  void (n as HTMLElement).offsetWidth;
  n.classList.add("flash");
  // its own fade's end, not one bubbling up from inside it (a thread card lit
  // within the event lit); or its fade cancelled, as a hidden tab cancels
  // it, so it does not light again when the tab comes back
  const done = (e: Event) => {
    if (e.target === n) unflash(n);
  };
  lit.set(n, done);
  n.addEventListener("animationend", done);
  n.addEventListener("animationcancel", done);
}

/** The ends a lit element waits for, so lighting it again or putting it out leaves none behind. */
const lit = new WeakMap<Element, (e: Event) => void>();

/** Put a light out, and stop waiting for its end. */
export function unflash(n: Element): void {
  n.classList.remove("flash");
  const done = lit.get(n);
  if (!done) return;
  n.removeEventListener("animationend", done);
  n.removeEventListener("animationcancel", done);
  lit.delete(n);
}

export interface TimelineHandle {
  scrollToSeqs(seqs: readonly number[]): void;
}

/**
 * The timeline, drawn again only when what it is given changes: its turns
 * are not memoized, so the page's own state changing — a thread's badge
 * opened on the review page — would otherwise draw every turn again.
 */
export const Timeline = memo(TimelineView);

function TimelineView({
  turns,
  names,
  handle,
  annotator,
  rater,
  startAtEnd = true,
  firstOpen = false,
  onPlay,
  playable,
  empty = t("session:noEvents"),
}: {
  turns: Turn[];
  names: Names;
  handle?: (h: TimelineHandle) => void;
  /** What an empty timeline says. */
  empty?: string;
  /** Under review: threads on each event and diff line. */
  annotator?: Annotator;
  /** An agent share: the person rates the answers to what they asked (byj-1wv.48). */
  rater?: Rater;
  /** Under review, a recording with a screen: play it from a turn. */
  onPlay?: (turn: number) => void;
  /** The turns the screen has a place for (review's castTurns): ▶ screen on those alone. */
  playable?: ReadonlySet<number>;
  /** A live share follows the latest event; a recording opens at its start. */
  startAtEnd?: boolean;
  /** A recording opens with its first turn unfolded and the rest folded, each a line to pick from (byj-1wv.51.4). */
  firstOpen?: boolean;
}) {
  // under memo its props do not change with the language, so it listens for it (i18n/Localized)
  useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(startAtEnd);
  // the turns folded away (byj-1wv.18): a new turn arrives open, a jump to a
  // seq unfolds the turn it is in
  const [folded, setFolded] = useState<ReadonlySet<number>>(() => new Set(firstOpen ? turns.slice(1).map((tn) => tn.turn) : []));
  const onToggle = useCallback((turn: number, open: boolean) => {
    setFolded((f) => {
      if (open === !f.has(turn)) return f;
      const next = new Set(f);
      if (open) next.delete(turn);
      else next.add(turn);
      return next;
    });
  }, []);
  const foldAll = useCallback(() => setFolded(new Set(turns.map((tn) => tn.turn))), [turns]);
  const unfoldAll = useCallback(() => setFolded(new Set()), []);
  const count = turns.reduce((n, tn) => n + tn.seqs.length, 0);

  // where it was scrolled to, while it is shown: a timeline hidden behind a
  // tab and shown again is put back there (byj-1wv.51.4), which Firefox,
  // letting a hidden box's scroll go, would not do
  const scrolledTo = useRef(0);
  const following = useRef(follow);
  following.current = follow;
  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el || el.clientHeight === 0) return;
    scrolledTo.current = el.scrollTop;
    // a recording, opened at its start, has no latest to follow: reaching
    // its end, as a jump to a thread there does, does not hold it there
    setFollow(startAtEnd && el.scrollHeight - el.scrollTop - el.clientHeight < FollowSlackPx);
  }, [startAtEnd]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== "function") return;
    let shown = el.clientHeight > 0;
    const watch = new ResizeObserver(() => {
      const now = el.clientHeight > 0;
      // following a live share's latest, it goes to the latest; else back to its place
      if (now && !shown) el.scrollTop = following.current ? el.scrollHeight : scrolledTo.current;
      shown = now;
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && follow) el.scrollTop = el.scrollHeight;
  }, [count, follow]);

  useEffect(() => {
    handle?.({
      scrollToSeqs(seqs) {
        const el = ref.current;
        if (!el) return;
        el.querySelectorAll(".flash").forEach(unflash);
        let first: Element | null = null;
        const unfold = new Set<number>();
        for (const s of seqs) {
          const n = el.querySelector(`[data-seqs~="${s}"]`);
          if (n) {
            flash(n);
            if (n instanceof HTMLDetailsElement) n.open = true;
            // a tool folded by hand keeps its result, and the threads on it, inside
            n.querySelectorAll(":scope > details").forEach((d) => ((d as HTMLDetailsElement).open = true));
            const turn = n.closest("details.turn");
            if (turn instanceof HTMLDetailsElement) {
              turn.open = true;
              unfold.add(Number(turn.dataset.turn));
            }
            first ??= n;
          }
        }
        if (unfold.size > 0) {
          setFolded((f) => {
            const next = new Set(f);
            for (const n of unfold) next.delete(n);
            return next;
          });
        }
        if (first) {
          setFollow(false);
          // Instant: a smooth scroll's first scroll event is still near the bottom and would re-arm auto-follow.
          first.scrollIntoView({ block: "center" });
        }
      },
    });
  }, [handle]);

  return (
    <div className="timeline-wrap">
      {turns.length > 1 && (
        <div className="timeline-tools">
          <button type="button" className="link small" onClick={foldAll} disabled={folded.size === turns.length}>
            {t("session:foldAll")}
          </button>
          <button type="button" className="link small" onClick={unfoldAll} disabled={folded.size === 0}>
            {t("session:unfoldAll")}
          </button>
        </div>
      )}
      <div className="timeline" ref={ref} onScroll={onScroll} data-testid="timeline">
        {turns.length === 0 && <p className="empty">{empty}</p>}
        {turns.map((tn) => (
          <TurnView
            key={tn.turn}
            turn={tn}
            names={names}
            annotator={annotator}
            rater={rater}
            onPlay={!playable || playable.has(tn.turn) ? onPlay : undefined}
            open={!folded.has(tn.turn)}
            onToggle={onToggle}
          />
        ))}
      </div>
      {!follow && startAtEnd && (
        <button className="jump" onClick={() => setFollow(true)}>
          {t("session:jumpToLatest")}
        </button>
      )}
    </div>
  );
}
